import React, {ReactNode, useMemo} from "react";
import './seo.scss';
import {useSiteMetadata} from "../../../services/hooks/useSiteMetadata";
import {Script} from "gatsby";
import routes from "../../../services/routes";

interface Props {
    /**
     * Use to force title
     */
    forceTitle?: string,
    title?: string,
    description?: string,
    pathname?: string,
    image?: string;
    children?: ReactNode,
}

/**
 * Seo recommendations for metadata
 */
const seoConfigReco = {
    titleMinLength: 30,
    titleMaxLength: 65,
    descMinLength: 70,
    descMaxLength: 160,
};

const Seo = ({
    forceTitle,
                 title,
                 description,
                 pathname,
                 image,
                 children,
             }: Props) => {
    const { siteMetadata, buildTime} = useSiteMetadata();
    const { title: defaultTitle, description: defaultDescription, defaultImage, siteUrl, twitterUsername } = siteMetadata;

    const finalImage = image ?? defaultImage;

    const finalTitle = useMemo(() => {
        if(forceTitle) return forceTitle;
        const inputTitle = `${title ?? defaultTitle} - Energic`;
        if(inputTitle.length > seoConfigReco.titleMaxLength) {
            return inputTitle.slice(0, seoConfigReco.titleMaxLength - 3) + '...';
        }
        return inputTitle;
    }, [title, defaultTitle]);

    const finalDesc = useMemo(() => {
        const inputDesc = description || defaultDescription;
        if(inputDesc.length > seoConfigReco.descMaxLength) {
            return inputDesc.slice(0, seoConfigReco.descMaxLength - 3) + '...';
        }
        return inputDesc;
    }, [description, defaultDescription]);

    const seo = {
        title: finalTitle,
        description: finalDesc,
        image: finalImage,
        url: `${siteUrl}${pathname || ``}`,
        twitterUsername,
    };

    // See https://github.com/LekoArts/gatsby-starter-prismic/blob/master/src/components/SEO/SEO.jsx
    // Also https://www.gatsbyjs.org/docs/seo/
    const schemaOrgWebPage = {
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        url: siteUrl,
        inLanguage: 'fr',
        mainEntityOfPage: siteUrl,
        description: seo.description,
        name: seo.title,
        copyrightYear: new Date().getFullYear(),
        datePublished: '2021-06-01',
        dateModified: buildTime,
        image: {
            '@type': 'ImageObject',
            url: finalImage,
        },
    };

    const itemListElement = [
        {
            '@type': 'ListItem',
            item: {
                '@id': `${siteUrl}${routes.challenge.home}`,
                name: 'Le Challenge Environnemental',
                description: 'pour impliquer chacun et passer à l’action',
            },
            position: 1,
        },
        {
            '@type': 'ListItem',
            item: {
                '@id': `${siteUrl}${routes.challenge.internal}`,
                name: 'Le challenge interne',
                description: 'de 1 à 3 mois pour embarquer l’ensemble des collaborateurs partout en France.',
            },
            position: 2,
        },
        {
            '@type': 'ListItem',
            item: {
                '@id': `${siteUrl}${routes.resources.footprint}`,
                name: 'Le Bilan carbone',
                description: 'pour calculer son empreinte carbone en moins de 5 min',
            },
            position: 3,

        },
        {
            '@type': 'ListItem',
            item: {
                '@id': `${siteUrl}${routes.team}`,
                name: 'L’Équipe',
                description: 'Tout savoir sur Energic',
            },
            position: 4,

        },
    ];

    const breadcrumb = {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        description: 'Breadcrumbs list',
        name: 'Breadcrumbs',
        itemListElement,
    };

    return (
        <>
            <meta charSet="utf-8"/>
            <html lang="fr"/>

            <title>{seo.title}</title>
            <meta name='title' property="og:title" content={seo.title}/>
            <meta name="description" property="og:description" content={seo.description}/>
            <meta name="image" property="og:image" content={seo.image}/>
            <meta property="og:url" content={seo.url}/>
            <meta name="twitter:card" content="summary_large_image"/>
            <meta name="twitter:title" content={seo.title}/>
            <meta name="twitter:url" content={seo.url}/>
            <meta name="twitter:description" content={seo.description}/>
            <meta name="twitter:image" content={seo.image}/>
            <meta name="twitter:creator" content={seo.twitterUsername}/>
            {/* Insert schema.org with gatsby Script */}
            <Script id='schemaOrgWebPageScript' type="application/ld+json">{JSON.stringify(schemaOrgWebPage)}</Script>
            <Script id='breadcrumbsScript' type="application/ld+json">{JSON.stringify(breadcrumb)}</Script>

            {
                // Disable search engines indexing when on staging
                process.env.NODE_ENV !== 'production' ? (
                    <meta name="robots" content="noindex"/>
                ) : null
            }
            {children}
        </>
    );
};

export default Seo;